import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { aiApi, handleAiError } from './api';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Loader2, Sparkles, Volume2, Trash2, Target, Wind, MessageSquare } from 'lucide-react';
import { toast } from 'sonner';
import { logger } from '../lib/logger';
import UsageNudge from './UsageNudge';

function speak(text) {
  if (!('speechSynthesis' in window)) {
    toast.error('Voice not supported on this browser.');
    return;
  }
  window.speechSynthesis.cancel();
  const u = new SpeechSynthesisUtterance(text);
  u.rate = 0.92;
  u.pitch = 1.0;
  window.speechSynthesis.speak(u);
}

export default function PreGame() {
  const navigate = useNavigate();
  const [sport, setSport] = useState('');
  const [situation, setSituation] = useState('');
  const [goal, setGoal] = useState('');
  const [minutes, setMinutes] = useState(60);
  const [generating, setGenerating] = useState(false);
  const [plan, setPlan] = useState(null);
  const [history, setHistory] = useState([]);
  const [usageTick, setUsageTick] = useState(0);

  const refresh = async () => {
    try {
      const { plans } = await aiApi.pregameHistory();
      setHistory(plans || []);
    } catch (err) {
      logger.error('Failed to load pregame history:', err);
    }
  };

  useEffect(() => { refresh(); }, []);

  const generate = async () => {
    if (!sport.trim() || !situation.trim() || !goal.trim()) {
      toast.error('Fill in sport, situation, and mindset goal.');
      return;
    }
    setGenerating(true);
    setPlan(null);
    try {
      const res = await aiApi.pregameGenerate({
        sport: sport.trim(),
        situation: situation.trim(),
        goal: goal.trim(),
        minutes_until: Number(minutes) || 60,
      });
      setPlan(res);
      refresh();
      setUsageTick((n) => n + 1);
      toast.success('Plan generated.');
    } catch (e) {
      handleAiError(e, toast, navigate);
      setUsageTick((n) => n + 1);
    } finally {
      setGenerating(false);
    }
  };

  const remove = async (plan_id) => {
    try {
      await aiApi.pregameDelete(plan_id);
      setHistory(history.filter((p) => p.plan_id !== plan_id));
      if (plan?.plan_id === plan_id) setPlan(null);
    } catch (e) {
      toast.error(e.message || 'Could not delete plan.');
    }
  };

  return (
    <div className="space-y-6" data-testid="pregame-page">
      <div>
        <div className="font-mono-accent uppercase tracking-[0.2em] text-[10px] text-[#004C54] font-semibold mb-2">Pre-Game Prep</div>
        <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight">Build Your Mental Plan</h1>
        <p className="text-sm text-foreground/70 mt-1">Tell me about the moment. I'll build the routine, visualization, and self-talk.</p>
      </div>

      <UsageNudge kind="pregame" refreshKey={usageTick} />

      <Card className="border-border">
        <CardContent className="p-5 sm:p-6 space-y-4">
          <div className="grid sm:grid-cols-2 gap-3">
            <div>
              <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Sport</label>
              <Input value={sport} onChange={(e) => setSport(e.target.value)} placeholder="e.g. Basketball" data-testid="pregame-sport" />
            </div>
            <div>
              <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Minutes until performance</label>
              <Input type="number" min={5} max={1440} value={minutes} onChange={(e) => setMinutes(e.target.value)} data-testid="pregame-minutes" />
            </div>
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Situation</label>
            <Input value={situation} onChange={(e) => setSituation(e.target.value)} placeholder="e.g. Playoff game vs rival" data-testid="pregame-situation" />
          </div>
          <div>
            <label className="text-xs font-semibold text-foreground/70 mb-1.5 block">Mindset goal</label>
            <Textarea value={goal} onChange={(e) => setGoal(e.target.value)} placeholder="e.g. Stay composed, trust my training, lock in on first 5 minutes" rows={2} data-testid="pregame-goal" />
          </div>
          <Button onClick={generate} disabled={generating} className="bg-[#004C54] hover:bg-[#003a40] text-white h-11 px-6" data-testid="pregame-generate">
            {generating ? <><Loader2 className="h-4 w-4 mr-2 animate-spin" />Generating...</> : <><Sparkles className="h-4 w-4 mr-2" />Generate plan</>}
          </Button>
        </CardContent>
      </Card>

      {plan && plan.plan && <PlanView data={plan.plan} />}

      {history.length > 0 && (
        <div data-testid="pregame-history">
          <h2 className="font-heading text-lg font-bold mb-3">Recent plans</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {history.map((p) => (
              <Card key={p.plan_id} className="border-border">
                <CardContent className="p-4">
                  <div className="flex items-start justify-between gap-2">
                    <button onClick={() => setPlan(p)} className="text-left flex-1" data-testid={`open-plan-${p.plan_id}`}>
                      <div className="font-heading text-sm font-bold mb-0.5">{p.plan?.title || `${p.sport} · ${p.situation}`}</div>
                      <div className="text-xs text-foreground/60">{p.sport} · {p.situation}</div>
                    </button>
                    <button onClick={() => remove(p.plan_id)} className="text-foreground/40 hover:text-destructive p-1" aria-label="Delete plan" data-testid={`delete-plan-${p.plan_id}`}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        </div>
      )}
    </div>
  );
}

function PlanView({ data }) {
  const { title, summary, routine = [], visualization, self_talk, breath } = data || {};
  return (
    <Card className="border-2 border-[#004C54]/30" data-testid="plan-view">
      <CardContent className="p-5 sm:p-6 space-y-5">
        <div>
          <h2 className="font-heading text-xl font-bold">{title}</h2>
          {summary && <p className="text-sm text-foreground/70 mt-1">{summary}</p>}
        </div>

        {routine.length > 0 && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Target className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading font-bold">Routine</h3>
            </div>
            <ol className="space-y-2">
              {routine.map((r, i) => (
                <li key={`${r.time}-${i}`} className="bg-[#F6F7F8] rounded-lg p-3">
                  <div className="text-xs font-mono-accent uppercase text-[#004C54] tracking-wider mb-0.5">{r.time}</div>
                  <div className="text-sm font-semibold">{r.step}</div>
                  {r.why && <div className="text-xs text-foreground/60 mt-1">{r.why}</div>}
                </li>
              ))}
            </ol>
          </div>
        )}

        {visualization?.script && (
          <div>
            <div className="flex items-center justify-between mb-2">
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#004C54]" />
                <h3 className="font-heading font-bold">Visualization</h3>
              </div>
              <Button size="sm" variant="outline" onClick={() => speak(visualization.script)} data-testid="play-visualization">
                <Volume2 className="h-4 w-4 mr-1.5" /> Play
              </Button>
            </div>
            <p className="text-sm leading-relaxed bg-[#F6F7F8] rounded-lg p-3">{visualization.script}</p>
            {Array.isArray(visualization.sensory_cues) && visualization.sensory_cues.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {visualization.sensory_cues.map((c) => (
                  <span key={c} className="text-xs bg-[#004C54]/10 text-[#004C54] rounded-full px-2.5 py-1 font-medium">{c}</span>
                ))}
              </div>
            )}
          </div>
        )}

        {self_talk && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <MessageSquare className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading font-bold">Self-Talk</h3>
            </div>
            {self_talk.reset_cue && (
              <div className="bg-[#0a0d0e] text-white rounded-lg p-3 mb-2">
                <div className="text-[10px] font-mono-accent uppercase tracking-wider text-[#A5ACAF] mb-0.5">Reset cue</div>
                <div className="text-sm font-semibold">{self_talk.reset_cue}</div>
              </div>
            )}
            {Array.isArray(self_talk.confidence_phrases) && (
              <ul className="space-y-1.5">
                {self_talk.confidence_phrases.map((p) => (
                  <li key={p} className="text-sm bg-[#F6F7F8] rounded-md px-3 py-2">“{p}”</li>
                ))}
              </ul>
            )}
          </div>
        )}

        {breath?.pattern && (
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Wind className="h-4 w-4 text-[#004C54]" />
              <h3 className="font-heading font-bold">Breath</h3>
            </div>
            <div className="bg-[#F6F7F8] rounded-lg p-3 text-sm">
              <span className="font-semibold">{breath.pattern}</span>
              {breath.reps ? <span className="text-foreground/60"> · {breath.reps} reps</span> : null}
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
